import React from "react";
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import Button from "../components/Button";
import Card from "../components/Card";
import CollapsibleSection from "../components/CollapsibleSection";
import SponsorBand from "../components/SponsorBand";
import { C } from "../theme";
import { db } from "../firebase";
import useResolvedVideoUrl from "../hooks/useResolvedVideoUrl";
import useUserPhotos from "../hooks/useUserPhotos";
import useTournamentRegions from "../hooks/useTournamentRegions";
import ProfileAvatar from "../components/ProfileAvatar";
import ProfilePhotoModal from "../components/ProfilePhotoModal";
import { getProfilePhotoUrl } from "../services/profilePhoto";
import { recordVideoView } from "../services/videoViews";

const slugify = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const toMs = (value) => {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return 0;
};

// Robust date coercion for tournament start/end which may be a Firestore
// Timestamp, a {seconds} object, epoch ms/seconds, or an ISO string.
const toMsAny = (value) => {
  if (value == null) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// A tournament is "active" when now falls within its start/end window. Missing
// bounds are treated as open so a tournament with no dates still shows up.
const isTournamentActive = (t) => {
  const now = Date.now();
  const start = toMsAny(t?.startDate);
  const end = toMsAny(t?.endDate);
  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
};

const formatCaughtAt = (value) => {
  const ms = toMs(value);
  if (!ms) return null;
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const parseWeightOz = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.round(value * 16));
  }

  const raw = (value || "").toString().toLowerCase().trim();
  if (!raw) return 0;

  const lbsMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:lb|lbs|pound|pounds)/);
  const ozMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:oz|ounce|ounces)/);

  if (lbsMatch || ozMatch) {
    const lbs = lbsMatch ? Number(lbsMatch[1]) : 0;
    const oz = ozMatch ? Number(ozMatch[1]) : 0;
    return Math.max(0, Math.round(lbs * 16 + oz));
  }

  const numeric = Number(raw.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.round(numeric * 16));
};

// ── Leaderboard board config (tournaments/{tid}.boardConfig) ─────────────────
// Written by the desktop "Leaderboard Studio". Drives how the mobile board
// scores, ranks, and displays. Missing/partial configs fall back to sane
// defaults so legacy tournaments keep rendering.
const DEFAULT_BOARD_CONFIG = {
  scoringMode: "total", // "total" | "aggregate"
  aggregateTopN: 5,
  tieBreak: "bigfish", // "bigfish" | "earliest" | "fewestdead"
  weightUnit: "decimal", // "decimal" | "lboz"
  lengthUnit: "in",
  showDeductions: false,
  deadFishDeductionLbs: 0,
  rowsVisible: 999, // 3 | 5 | 10 | 999 (all)
  bigFish: true,
  showDQ: true,
};

function resolveBoardConfig(tournamentDoc) {
  const c =
    tournamentDoc && typeof tournamentDoc.boardConfig === "object" && tournamentDoc.boardConfig
      ? tournamentDoc.boardConfig
      : {};
  const rowsVisible = Number(c.rowsVisible);
  return {
    ...DEFAULT_BOARD_CONFIG,
    ...c,
    scoringMode: c.scoringMode === "aggregate" ? "aggregate" : "total",
    aggregateTopN: Math.max(1, Number(c.aggregateTopN) || DEFAULT_BOARD_CONFIG.aggregateTopN),
    tieBreak: ["bigfish", "earliest", "fewestdead"].includes(c.tieBreak) ? c.tieBreak : "bigfish",
    weightUnit: c.weightUnit === "lboz" ? "lboz" : "decimal",
    rowsVisible: [3, 5, 10, 999].includes(rowsVisible) ? rowsVisible : 999,
    showDeductions: !!c.showDeductions,
    deadFishDeductionLbs: Number(c.deadFishDeductionLbs) || 0,
    bigFish: c.bigFish !== false,
    showDQ: c.showDQ !== false,
  };
}

// Weight display honoring boardConfig.weightUnit. All source weights are ounces.
const formatBoardWeight = (oz, weightUnit) => {
  const v = Number(oz) || 0;
  if (v <= 0) return weightUnit === "lboz" ? "0lb 0oz" : "0.00 lb";
  if (weightUnit === "lboz") {
    const lbs = Math.floor(v / 16);
    const rem = Math.round(v % 16);
    return `${lbs}lb ${rem}oz`;
  }
  return `${(v / 16).toFixed(2)} lb`;
};

// Rank comparator driven by boardConfig: score desc, then the configured tie
// break. Assumes callers already separate DQ teams (which sort last).
function boardRankCompare(a, b, cfg) {
  if (a.totalOz !== b.totalOz) return b.totalOz - a.totalOz;
  if (cfg.tieBreak === "earliest") {
    const ax = a.earliestCatchAt || Infinity;
    const bx = b.earliestCatchAt || Infinity;
    if (ax !== bx) return ax - bx; // earliest catch wins
  } else if (cfg.tieBreak === "fewestdead") {
    if (a.deadCount !== b.deadCount) return a.deadCount - b.deadCount;
  } else {
    // "bigfish" (default): larger single fish wins
    if (a.bigFishOz !== b.bigFishOz) return b.bigFishOz - a.bigFishOz;
  }
  return (a.team.teamName || a.team.id || "").localeCompare(b.team.teamName || b.team.id || "");
}

function VideoThumbnail({ src }) {
  const videoRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const handleMeta = () => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = 0.01;
  };

  const handleSeeked = () => setReady(true);
  const handleError = () => setFailed(true);

  return (
    <div style={{
      width: 72,
      height: 54,
      borderRadius: 7,
      overflow: "hidden",
      flexShrink: 0,
      background: C.midnight,
      border: `1px solid ${C.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      {failed ? (
        <span style={{ fontSize: 18, opacity: 0.4 }}>🎬</span>
      ) : (
        <video
          ref={videoRef}
          src={src}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={handleMeta}
          onSeeked={handleSeeked}
          onError={handleError}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: ready ? "block" : "none",
          }}
        />
      )}
      {!ready && !failed && (
        <span style={{ fontSize: 18, opacity: 0.4, position: "absolute" }}>🎬</span>
      )}
    </div>
  );
}

// Expanded body of a video list row. Resolves the playable URL (durable on
// Firebase, on-demand presigned GET on R2) only when the row is expanded.
function ExpandedVideoBody({ video, parts }) {
  const url = useResolvedVideoUrl(video.storagePath, video.downloadUrl);
  return (
    <div style={{ padding: "0 12px 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
      {url && <VideoThumbnail src={url} />}
      <div style={{ fontSize: 10, color: C.white, opacity: 0.9 }}>
        {parts.length > 0 ? parts.join(" · ") : "Catch Stats Not Available"}
      </div>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={() => recordVideoView(video.id)}
          style={{ fontSize: 11, color: C.seafoam, textDecoration: "none" }}
        >
          Open video ↗
        </a>
      )}
    </div>
  );
}

export default function TournamentScreen({ userId, profile, onSignOut }) {
  const scrollContainerRef = React.useRef(null);
  const [tournamentDocs, setTournamentDocs] = React.useState([]);
  const [tournamentSearch, setTournamentSearch] = React.useState("");
  const [regionFilter, setRegionFilter] = React.useState("");
  const [sponsorEmailInput, setSponsorEmailInput] = React.useState("");
  const [addSponsorError, setAddSponsorError] = React.useState("");
  const [addingSponsor, setAddingSponsor] = React.useState(false);
  const [teams, setTeams] = React.useState([]);
  const [videos, setVideos] = React.useState([]);
  const [expandedVideoIds, setExpandedVideoIds] = React.useState(() => new Set());
  const [allTeams, setAllTeams] = React.useState([]);
  const [selectedTeamId, setSelectedTeamId] = React.useState("");
  const [selectedCatchId, setSelectedCatchId] = React.useState("");
  const [catchesByTeam, setCatchesByTeam] = React.useState({});
  const [loadingCatches, setLoadingCatches] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [teamSearch, setTeamSearch] = React.useState("");
  const [selectedExistingTeamId, setSelectedExistingTeamId] = React.useState("");
  const [addingTeam, setAddingTeam] = React.useState(false);
  const [addTeamError, setAddTeamError] = React.useState("");
  const [addTeamSuccess, setAddTeamSuccess] = React.useState("");
  const [confirmRemoveTeam, setConfirmRemoveTeam] = React.useState(null);
  const [removingTeamId, setRemovingTeamId] = React.useState("");
  const [removeTeamError, setRemoveTeamError] = React.useState("");
  const [seenCatchIdsByTeam, setSeenCatchIdsByTeam] = React.useState({});
  const initializedSeenRef = React.useRef(false);
  const [showExistingTeams, setShowExistingTeams] = React.useState(false);
  const [showTeams, setShowTeams] = React.useState(true);
  const [showCatches, setShowCatches] = React.useState(true);
  const [leaderboardLoadError, setLeaderboardLoadError] = React.useState("");
  // WS6 hybrid: the dashboard materializes standings into leaderboards/{tid}.
  const [leaderboardDoc, setLeaderboardDoc] = React.useState(null);
  // WS5 read-only dashboard state: DQ (from registrations) + boat assignments.
  const [registrationsMap, setRegistrationsMap] = React.useState({});
  const [boatAssignmentsMap, setBoatAssignmentsMap] = React.useState({});

  const role = profile?.role || "";
  const followingTeamIds = profile?.followingTeamIds || [];
  const [selectedTournamentName, setSelectedTournamentName] = React.useState(
    profile?.tournamentName || ""
  );

  // Account header (profile photo + logout) for the view-only roles, which no
  // longer see the dashboard where those controls normally live.
  const [showProfilePhoto, setShowProfilePhoto] = React.useState(false);
  const profilePhotoUrl = getProfilePhotoUrl(profile);
  const profileDisplayName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const accountHeader = (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 10, paddingRight: 40 }}>
      <ProfileAvatar
        url={profilePhotoUrl}
        name={profileDisplayName}
        size={30}
        onClick={() => setShowProfilePhoto(true)}
        title="View profile"
      />
      <button
        type="button"
        onClick={() => onSignOut && onSignOut()}
        style={{
          padding: "7px 14px",
          borderRadius: 999,
          border: `1px solid ${C.border}`,
          background: "transparent",
          color: C.white,
          fontSize: 11,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        Log out
      </button>
      <ProfilePhotoModal
        open={showProfilePhoto}
        onClose={() => setShowProfilePhoto(false)}
        url={profilePhotoUrl}
        name={profileDisplayName}
        teamName={profile?.teamName || ""}
      />
    </div>
  );

  React.useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "tournaments"),
      (snap) => {
        const docsData = snap.docs
          .map((item) => ({ id: item.id, ...(item.data() || {}) }))
          .sort((a, b) =>
            String(a.name || a.id || "").localeCompare(String(b.name || b.id || ""))
          );
        setTournamentDocs(docsData);
      },
      () => {
        setTournamentDocs([]);
      }
    );
    return () => unsub();
  }, []);

  const myUid = String(userId || "");
  const myEmail = String(profile?.email || "").trim().toLowerCase();
  const isSelectableRole =
    role === "director" || role === "participant" || role === "sponsor";

  const activeTournaments = React.useMemo(
    () => tournamentDocs.filter(isTournamentActive),
    [tournamentDocs]
  );
  // Region (US state / province) per tournament, derived from its geofence
  // center. Lets Spectators and Sponsors browse tournaments by region.
  const { regionByTournamentId, regions } = useTournamentRegions(activeTournaments);
  const createdTournaments = React.useMemo(
    () =>
      activeTournaments.filter(
        (t) =>
          (t.createdByUid && t.createdByUid === myUid) ||
          (t.directorId && t.directorId === myUid) ||
          (myEmail && String(t.directorEmail || "").trim().toLowerCase() === myEmail)
      ),
    [activeTournaments, myUid, myEmail]
  );
  const sponsoredTournaments = React.useMemo(
    () =>
      activeTournaments.filter((t) => {
        const ids = Array.isArray(t.sponsorIds) ? t.sponsorIds : [];
        const emails = Array.isArray(t.sponsorEmails)
          ? t.sponsorEmails.map((e) => String(e || "").trim().toLowerCase())
          : [];
        return (myUid && ids.includes(myUid)) || (myEmail && emails.includes(myEmail));
      }),
    [activeTournaments, myUid, myEmail]
  );
  const searchedTournaments = React.useMemo(() => {
    const q = tournamentSearch.trim().toLowerCase();
    let list = activeTournaments;
    if (regionFilter) {
      list = list.filter((t) => (regionByTournamentId[t.id] || "") === regionFilter);
    }
    if (q) {
      list = list.filter((t) => {
        const name = String(t.name || t.tournamentId || t.id || "").toLowerCase();
        const region = String(regionByTournamentId[t.id] || "").toLowerCase();
        return name.includes(q) || region.includes(q);
      });
    }
    return list.slice(0, 25);
  }, [activeTournaments, tournamentSearch, regionFilter, regionByTournamentId]);

  // Auto-select a sensible default for view-only roles: directors land on their
  // first created tournament, sponsors on their first sponsored one; spectators
  // must search (no auto-select).
  React.useEffect(() => {
    if (!isSelectableRole || selectedTournamentName) return;
    const preferred =
      role === "director"
        ? createdTournaments[0]
        : role === "sponsor"
        ? sponsoredTournaments[0]
        : null;
    if (preferred?.name) setSelectedTournamentName(preferred.name);
  }, [isSelectableRole, role, selectedTournamentName, createdTournaments, sponsoredTournaments]);

  React.useEffect(() => {
    if (!isSelectableRole || !profile?.tournamentName) return;
    setSelectedTournamentName((prev) => prev || profile.tournamentName);
  }, [isSelectableRole, profile?.tournamentName]);

  const tournamentName = isSelectableRole
    ? selectedTournamentName
    : (profile?.tournamentName || "");

  // Resolve the selected tournament to its Firestore doc so `tournamentId` is
  // ALWAYS the real doc id (never a name slug) — matching the dashboard's
  // writes and its collectionGroup queries. Selectable roles match on the
  // picked name; anglers carry the real doc id (or a legacy slug) on profile.
  const selectedTournamentDoc = React.useMemo(() => {
    if (isSelectableRole) {
      const name = String(selectedTournamentName || "").trim();
      if (!name) return null;
      const nameKey = slugify(name);
      return (
        tournamentDocs.find((t) => String(t.name || "").trim() === name) ||
        tournamentDocs.find((t) => slugify(t.name || t.id || "") === nameKey) ||
        null
      );
    }
    const profTid = String(profile?.tournamentId || "").trim();
    const profNameKey = slugify(profile?.tournamentName || "");
    return (
      tournamentDocs.find((t) => t.id === profTid) ||
      tournamentDocs.find(
        (t) =>
          (profTid &&
            (slugify(t.tournamentId || t.name || t.id || "") === slugify(profTid) ||
              String(t.tournamentId || "") === profTid)) ||
          (profNameKey && slugify(t.name || t.id || "") === profNameKey)
      ) ||
      null
    );
  }, [isSelectableRole, tournamentDocs, selectedTournamentName, profile?.tournamentId, profile?.tournamentName]);

  const tournamentId = isSelectableRole
    ? (selectedTournamentDoc?.id || "")
    : (selectedTournamentDoc?.id || String(profile?.tournamentId || "").trim());

  React.useEffect(() => {
    if (role !== "director") {
      setAllTeams([]);
      return undefined;
    }

    const unsub = onSnapshot(
      collection(db, "teams"),
      (snap) => {
        const next = snap.docs
          .map((item) => ({ id: item.id, ...(item.data() || {}) }))
          .sort((a, b) => (a.teamName || "").localeCompare(b.teamName || ""));
        setAllTeams(next);
      },
      () => setAllTeams([])
    );

    return () => unsub();
  }, [role]);

  React.useEffect(() => {
    if (!tournamentId) {
      setTeams([]);
      setSelectedTeamId("");
      setSeenCatchIdsByTeam({});
      setLeaderboardLoadError("");
      initializedSeenRef.current = false;
      return undefined;
    }

    if (role === "director") {
      const next = allTeams
        .filter((team) => (team.tournamentId || "") === tournamentId)
        .sort((a, b) => (a.teamName || "").localeCompare(b.teamName || ""));
      setLeaderboardLoadError("");
      setTeams(next);
      setSelectedTeamId((prev) => (prev && next.some((team) => team.id === prev) ? prev : (next[0]?.id || "")));
      return undefined;
    }

    const teamsQuery = query(collection(db, "teams"), where("tournamentId", "==", tournamentId));
    const unsub = onSnapshot(
      teamsQuery,
      (snap) => {
        setLeaderboardLoadError("");
        const next = snap.docs
          .map((item) => ({ id: item.id, ...(item.data() || {}) }))
          .sort((a, b) => (a.teamName || "").localeCompare(b.teamName || ""));
        setTeams(next);
        setSelectedTeamId((prev) => (prev && next.some((team) => team.id === prev) ? prev : (next[0]?.id || "")));
      },
      () => {
        setTeams([]);
        setSelectedTeamId("");
        setLeaderboardLoadError("Unable to load leaderboard data.");
      }
    );
    return () => unsub();
  }, [role, tournamentId, allTeams]);

  React.useEffect(() => {
    if (!tournamentId) {
      setVideos([]);
      return undefined;
    }

    const videosQuery = query(collection(db, "videos"), where("tournamentId", "==", tournamentId));
    const unsub = onSnapshot(
      videosQuery,
      (snap) => {
        const next = snap.docs
          .map((item) => ({ id: item.id, ...(item.data() || {}) }))
          .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
        setVideos(next);
      },
      () => setVideos([])
    );

    return () => unsub();
  }, [tournamentId]);

  // WS6: subscribe to the dashboard's materialized leaderboard (client read-only).
  React.useEffect(() => {
    if (!tournamentId) {
      setLeaderboardDoc(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, "leaderboards", tournamentId),
      (snap) => setLeaderboardDoc(snap.exists() ? (snap.data() || null) : null),
      () => setLeaderboardDoc(null)
    );
    return () => unsub();
  }, [tournamentId]);

  // WS5: registrations carry disqualification state (director-set on the dashboard).
  React.useEffect(() => {
    if (!tournamentId) {
      setRegistrationsMap({});
      return undefined;
    }
    const unsub = onSnapshot(
      collection(db, "tournaments", tournamentId, "registrations"),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          map[String(data.teamId || d.id)] = data;
        });
        setRegistrationsMap(map);
      },
      () => setRegistrationsMap({})
    );
    return () => unsub();
  }, [tournamentId]);

  // WS5: flight-coordinator boat assignments (display only; never written here).
  React.useEffect(() => {
    if (!tournamentId) {
      setBoatAssignmentsMap({});
      return undefined;
    }
    const unsub = onSnapshot(
      collection(db, "tournaments", tournamentId, "boatAssignments"),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          map[String(data.teamId || d.id)] = data;
        });
        setBoatAssignmentsMap(map);
      },
      () => setBoatAssignmentsMap({})
    );
    return () => unsub();
  }, [tournamentId]);

  React.useEffect(() => {
    if (role !== "director" || !tournamentId || teams.length === 0) {
      setCatchesByTeam({});
      setSelectedCatchId("");
      setSeenCatchIdsByTeam({});
      initializedSeenRef.current = false;
      return undefined;
    }

    let cancelled = false;
    const loadTeamCatches = async () => {
      setLoadingCatches(true);
      try {
        const next = {};

        for (const team of teams) {
          const memberUserIds = Array.isArray(team.memberUserIds) ? team.memberUserIds : [];
          const allCatches = [];

          for (const memberUserId of memberUserIds) {
            const sessionsSnap = await getDocs(collection(db, "users", memberUserId, "sessions"));
            for (const sessionItem of sessionsSnap.docs) {
              const catchesSnap = await getDocs(
                collection(db, "users", memberUserId, "sessions", sessionItem.id, "catches")
              );

              catchesSnap.docs.forEach((catchItem) => {
                const catchData = catchItem.data() || {};
                const fallbackVideo = videos.find((video) =>
                  video?.catchRef?.userId === memberUserId
                  && video?.catchRef?.sessionId === sessionItem.id
                  && video?.catchRef?.catchId === catchItem.id
                );

                const lengthValue = typeof catchData.lengthIn === "number"
                  ? `${catchData.lengthIn.toFixed(1).replace(/\.0$/, "")} in`
                  : catchData.lengthIn || catchData.size || "—";
                const weightValue = typeof catchData.weightLbs === "number"
                  ? `${catchData.weightLbs.toFixed(1).replace(/\.0$/, "")} lbs`
                  : catchData.weightLbs || catchData.weight || "—";

                allCatches.push({
                  id: `${memberUserId}_${sessionItem.id}_${catchItem.id}`,
                  userId: memberUserId,
                  sessionId: sessionItem.id,
                  catchId: catchItem.id,
                  teamId: team.id,
                  teamName: team.teamName || team.id,
                  caughtAt: catchData.caughtAt || null,
                  species: catchData.species || fallbackVideo?.catch?.species || "Unknown",
                  length: lengthValue,
                  weight: weightValue,
                  reviewStatus: catchData.reviewStatus || "pending",
                  boundaryStatus: "TRUE",
                  videoUrl: catchData.videoDownloadUrl || fallbackVideo?.downloadUrl || "",
                  videoStoragePath: catchData.videoStoragePath || fallbackVideo?.storagePath || "",
                });
              });
            }
          }

          next[team.id] = allCatches.sort((a, b) => toMs(b.caughtAt) - toMs(a.caughtAt));
        }

        if (!cancelled) {
          setCatchesByTeam(next);
        }
      } catch {
        if (!cancelled) setCatchesByTeam({});
      } finally {
        if (!cancelled) setLoadingCatches(false);
      }
    };

    loadTeamCatches();
    return () => {
      cancelled = true;
    };
  }, [role, tournamentId, teams, videos]);

  React.useEffect(() => {
    setSelectedCatchId("");
  }, [selectedTeamId]);

  React.useEffect(() => {
    if (role !== "director") return;
    const teamIds = Object.keys(catchesByTeam);
    if (teamIds.length === 0) return;

    if (!initializedSeenRef.current) {
      const initialSeen = {};
      teamIds.forEach((teamId) => {
        initialSeen[teamId] = (catchesByTeam[teamId] || []).map((item) => item.id);
      });
      setSeenCatchIdsByTeam(initialSeen);
      initializedSeenRef.current = true;
    }
  }, [role, catchesByTeam]);

  const setFollowedTeams = async (nextTeamIds) => {
    if (!userId) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, "users", userId),
        {
          followingTeamIds: nextTeamIds,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleFollowTeam = (teamId) => {
    const next = followingTeamIds.includes(teamId)
      ? followingTeamIds.filter((id) => id !== teamId)
      : [...followingTeamIds, teamId];
    setFollowedTeams(next);
  };

  const visibleVideos = role === "participant" && followingTeamIds.length > 0
    ? videos.filter((video) => followingTeamIds.includes(video.teamId))
    : videos;

  // Other anglers' avatars for the certified-video rows come from the public
  // profile mirror (users/{uid} is self-only, so we can't read their photo).
  const anglerPhotos = useUserPhotos(visibleVideos.map((v) => v.userId));

  const filteredExistingTeams = React.useMemo(() => {
    const seenNames = new Set();
    const uniqueTeams = allTeams.filter((team) => {
      const name = (team.teamName || "").trim().toLowerCase();
      if (!name) return false;
      if (seenNames.has(name)) return false;
      seenNames.add(name);
      return true;
    });

    const q = teamSearch.trim().toLowerCase();
    if (!q) return uniqueTeams.slice(0, 12);
    return uniqueTeams.filter((team) => (team.teamName || "").toLowerCase().includes(q)).slice(0, 12);
  }, [allTeams, teamSearch]);

  const selectedTeam = teams.find((team) => team.id === selectedTeamId) || null;
  const selectedTeamCatches = role === "director"
    ? (catchesByTeam[selectedTeamId] || [])
    : [];
  const selectedCatch = selectedTeamCatches.find((item) => item.id === selectedCatchId) || null;
  const selectedVideoUrl = useResolvedVideoUrl(
    selectedCatch?.videoStoragePath,
    selectedCatch?.videoUrl
  );

  const teamHasNewCatch = React.useCallback(
    (teamId) => {
      const currentIds = (catchesByTeam[teamId] || []).map((item) => item.id);
      const seenIds = seenCatchIdsByTeam[teamId] || [];
      return currentIds.some((id) => !seenIds.includes(id));
    },
    [catchesByTeam, seenCatchIdsByTeam]
  );

  const orderedTeams = React.useMemo(() => {
    if (role !== "director") return teams;
    return [...teams].sort((a, b) => {
      const aNew = teamHasNewCatch(a.id) ? 1 : 0;
      const bNew = teamHasNewCatch(b.id) ? 1 : 0;
      if (aNew !== bNew) return bNew - aNew;

      const aLatest = toMs((catchesByTeam[a.id] || [])[0]?.caughtAt);
      const bLatest = toMs((catchesByTeam[b.id] || [])[0]?.caughtAt);
      if (aLatest !== bLatest) return bLatest - aLatest;
      return (a.teamName || "").localeCompare(b.teamName || "");
    });
  }, [role, teams, catchesByTeam, teamHasNewCatch]);

  // Board config from the tournament doc (desktop Leaderboard Studio). Drives
  // scoring, tie-break, weight units, DQ visibility, row count, deductions.
  const boardConfig = React.useMemo(
    () => resolveBoardConfig(selectedTournamentDoc),
    [selectedTournamentDoc]
  );

  const leaderboardRows = React.useMemo(() => {
    const dqTeamIds = new Set(
      Object.entries(registrationsMap)
        .filter(([, reg]) => reg?.disqualified)
        .map(([tid]) => tid)
    );

    // WS6 hybrid: prefer the dashboard's materialized standings when present so
    // mobile ranks identically to the director dashboard (and spectators, who
    // don't load raw catch data, still get real numbers). Fall back to on-device
    // compute when the leaderboards/{tid} doc hasn't been written yet.
    const materialized = Array.isArray(leaderboardDoc?.rows) ? leaderboardDoc.rows : null;
    if (materialized && materialized.length > 0) {
      // Score + rank client-side per boardConfig. All weights are ounces.
      const mode = boardConfig.scoringMode;
      const topN = boardConfig.aggregateTopN;
      const scored = materialized.map((r) => {
        const teamId = String(r.teamId || "");
        const topFishOz = Array.isArray(r.topFishOz) ? r.topFishOz.map((v) => Number(v) || 0) : [];
        const topFishCaughtAt = Array.isArray(r.topFishCaughtAt) ? r.topFishCaughtAt : [];
        const totalWeightOz = Number(r.totalWeightOz) || 0;
        const bigFishOz = Number(r.bigFishOz) || (topFishOz[0] || 0);
        const deadCount = Number(r.deadCount) || 0;
        // score: "total" = every catch; "aggregate" = sum of the top N fish.
        const score =
          mode === "aggregate"
            ? topFishOz.slice(0, topN).reduce((s, v) => s + v, 0)
            : totalWeightOz;
        return {
          team: { id: teamId, teamName: r.teamName || teamId },
          rounds: [0, 1, 2].map((i) => topFishOz[i] || 0),
          totalOz: score,
          totalWeightOz,
          bigFishOz,
          deadCount,
          earliestCatchAt: Number(topFishCaughtAt[0]) || 0,
          catchCount: Number(r.catchCount) || 0,
          hasNew: teamHasNewCatch(teamId),
          latestCaughtAt: Number(r.latestCatchAt) || 0,
          disqualified: dqTeamIds.has(teamId) || !!r.disqualified,
          fromMaterialized: true,
        };
      });
      // Active teams first (ranked by score + tie-break); DQ teams last (rank null).
      scored.sort((a, b) => {
        if (a.disqualified !== b.disqualified) return a.disqualified ? 1 : -1;
        return boardRankCompare(a, b, boardConfig);
      });
      let rank = 0;
      return scored.map((row) =>
        row.disqualified ? { ...row, position: null } : { ...row, position: (rank += 1) }
      );
    }

    // Fallback: on-device compute. Honor DQ (excluded from standings) and skip
    // denied catches so it stays as close to the dashboard as local data allows.
    const baseTeams = role === "director" ? orderedTeams : teams;
    const rows = baseTeams.map((team) => {
      const disqualified = dqTeamIds.has(team.id);
      const eligibleCatches = (catchesByTeam[team.id] || []).filter(
        (item) => String(item.reviewStatus || "").toLowerCase() !== "denied"
      );
      const topWeights = disqualified
        ? []
        : [...eligibleCatches]
            .map((item) => parseWeightOz(item.weight))
            .filter((oz) => oz > 0)
            .sort((a, b) => b - a)
            .slice(0, 2);

      const rounds = [0, 0, 0].map((_, idx) => topWeights[idx] || 0);
      const totalOz = rounds.reduce((sum, val) => sum + val, 0);
      const latestCaughtAt = toMs(eligibleCatches[0]?.caughtAt);

      return {
        team,
        rounds,
        totalOz,
        catchCount: (catchesByTeam[team.id] || []).length,
        hasNew: teamHasNewCatch(team.id),
        latestCaughtAt,
        disqualified,
      };
    });

    return rows
      .sort((a, b) => {
        if (a.disqualified !== b.disqualified) return a.disqualified ? 1 : -1;
        if (a.totalOz !== b.totalOz) return b.totalOz - a.totalOz;
        if (a.latestCaughtAt !== b.latestCaughtAt) return b.latestCaughtAt - a.latestCaughtAt;
        return (a.team.teamName || a.team.id || "").localeCompare(b.team.teamName || b.team.id || "");
      })
      .map((row, idx) => ({ ...row, position: idx + 1 }));
  }, [leaderboardDoc, registrationsMap, role, orderedTeams, teams, catchesByTeam, teamHasNewCatch, boardConfig]);

  // Apply board-config display rules: hide DQ teams unless showDQ, then cap to
  // rowsVisible (3 | 5 | 10 | 999=all).
  const visibleLeaderboardRows = React.useMemo(() => {
    let rows = leaderboardRows;
    if (!boardConfig.showDQ) rows = rows.filter((r) => !r.disqualified);
    const limit = Number(boardConfig.rowsVisible) || 999;
    if (limit && limit < 999) rows = rows.slice(0, limit);
    return rows;
  }, [leaderboardRows, boardConfig.showDQ, boardConfig.rowsVisible]);

  // Sponsor logos placed on this tournament's board (written by the dashboard's
  // Leaderboard Studio). Each carries an optional linkUrl so tapping the logo
  // opens the sponsor's site. Deduped by image so a sponsor on multiple slots
  // shows once in the band.
  const boardSponsors = React.useMemo(() => {
    const arr = Array.isArray(selectedTournamentDoc?.sponsors) ? selectedTournamentDoc.sponsors : [];
    const seen = new Set();
    return arr
      .map((s) => ({ imageUrl: s.logoUrl || "", linkUrl: s.linkUrl || "", name: s.companyName || "Sponsor" }))
      .filter((s) => {
        if (!s.imageUrl || seen.has(s.imageUrl)) return false;
        seen.add(s.imageUrl);
        return true;
      });
  }, [selectedTournamentDoc]);

  const handleAddExistingTeam = async () => {
    if (!tournamentId || !selectedExistingTeamId) return;
    const source = allTeams.find((team) => team.id === selectedExistingTeamId);
    if (!source?.teamName) return;

    setAddTeamError("");
    setAddTeamSuccess("");
    setAddingTeam(true);
    try {
      // Contract: key/link by the REAL tournament doc id + the team's canonical
      // id (never a name slug). Write BOTH signals the dashboard reads: the
      // team-doc link (2b) and a registration doc (2a).
      const realTeamId = String(source.teamId || source.id || "").trim();
      const members = Array.isArray(source.memberUserIds) ? source.memberUserIds : [];

      // 1) Link the team doc to this tournament.
      await setDoc(
        doc(db, "teams", realTeamId),
        {
          teamId: realTeamId,
          teamName: source.teamName,
          name: source.teamName,
          tournamentId,
          tournamentName: tournamentName || selectedTournamentName,
          captainUserId: source.captainUserId || "",
          memberUserIds: members,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // 2) Registration doc (dashboard Registrations / Tournament Anglers).
      //    One per team per tournament — dedupe by teamId first.
      const regCol = collection(db, "tournaments", tournamentId, "registrations");
      const existingReg = await getDocs(query(regCol, where("teamId", "==", realTeamId)));
      if (existingReg.empty) {
        await addDoc(regCol, {
          teamId: realTeamId,
          teamName: source.teamName,
          anglers: Array.isArray(source.anglers) ? source.anglers : [],
          tournamentId,
          tournamentName: tournamentName || selectedTournamentName,
          assignedByUid: userId,
          assignedAt: serverTimestamp(),
        });
      }

      setSelectedTeamId(realTeamId);
      setSelectedExistingTeamId("");
      setTeamSearch("");
      setAddTeamSuccess(
        `\u2713 ${source.teamName} added to ${tournamentName || selectedTournamentName || "this tournament"}.`
      );
    } catch (err) {
      setAddTeamError(err?.message || "Could not add team to tournament.");
    } finally {
      setAddingTeam(false);
    }
  };

  // Remove a team's association with the currently selected tournament by
  // deleting the tournament-scoped team doc AND its registration doc. The
  // angler's source team (and any history under other tournaments) is untouched.
  const handleRemoveTeam = async (team) => {
    if (!team?.id) return;
    setRemoveTeamError("");
    setRemovingTeamId(team.id);
    try {
      await deleteDoc(doc(db, "teams", team.id));
      if (tournamentId) {
        const teamKey = String(team.teamId || team.id).trim();
        const regCol = collection(db, "tournaments", tournamentId, "registrations");
        const regs = await getDocs(query(regCol, where("teamId", "==", teamKey)));
        await Promise.all(regs.docs.map((d) => deleteDoc(d.ref)));
      }
      setConfirmRemoveTeam(null);
      setSelectedTeamId((prev) => (prev === team.id ? "" : prev));
    } catch (err) {
      setRemoveTeamError(err?.message || "Could not remove team from tournament.");
    } finally {
      setRemovingTeamId("");
    }
  };

  const handleAddSponsor = async () => {
    const email = sponsorEmailInput.trim().toLowerCase();
    setAddSponsorError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAddSponsorError("Enter a valid sponsor email address.");
      return;
    }
    const targetId = selectedTournamentDoc?.id || tournamentId;
    if (!targetId) {
      setAddSponsorError("Select a tournament first.");
      return;
    }
    setAddingSponsor(true);
    try {
      await setDoc(
        doc(db, "tournaments", targetId),
        { sponsorEmails: arrayUnion(email), updatedAt: serverTimestamp() },
        { merge: true }
      );
      setSponsorEmailInput("");
    } catch (err) {
      setAddSponsorError(err?.message || "Could not add sponsor.");
    } finally {
      setAddingSponsor(false);
    }
  };

  // Role-appropriate tournament chooser: directors see the tournaments they
  // created; sponsors see the ones they're linked to plus a search; spectators
  // search all active tournaments. Selecting one drives the leaderboard below.
  const renderTournamentPicker = () => {
    const pick = (t) => {
      setSelectedTournamentName(String(t.name || t.id || "").trim());
      setTournamentSearch("");
    };
    const pill = (t) => {
      const name = String(t.name || t.id || "").trim();
      const selected = selectedTournamentName === name;
      const region = regionByTournamentId[t.id] || "";
      return (
        <button
          key={t.id}
          type="button"
          onClick={() => pick(t)}
          style={{
            textAlign: "left",
            borderRadius: 10,
            border: `1px solid ${selected ? C.seafoam : "#1e3448"}`,
            background: selected ? "rgba(46,191,165,0.12)" : "transparent",
            color: C.white,
            padding: "10px 12px",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 700,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <span>{name}</span>
          {region ? (
            <span style={{ fontSize: 9, color: C.teal, fontWeight: 600, letterSpacing: 0.5 }}>
              📍 {region}
            </span>
          ) : null}
        </button>
      );
    };
    const label = (text) => (
      <div style={{ fontSize: 10, color: C.teal, letterSpacing: 2, marginBottom: 8 }}>{text}</div>
    );
    const box = { border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.cardBg };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 12 }}>
        {role === "director" && (
          <div style={box}>
            {label("YOUR TOURNAMENTS")}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {createdTournaments.length === 0 ? (
                <div style={{ fontSize: 11, color: C.muted }}>
                  You haven't created any active tournaments.
                </div>
              ) : (
                createdTournaments.map(pill)
              )}
            </div>
          </div>
        )}
        {role === "sponsor" && (
          <div style={box}>
            {label("TOURNAMENTS YOU SPONSOR")}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sponsoredTournaments.length === 0 ? (
                <div style={{ fontSize: 11, color: C.muted }}>
                  You aren't linked to any active tournaments yet.
                </div>
              ) : (
                sponsoredTournaments.map(pill)
              )}
            </div>
          </div>
        )}
        {(role === "participant" || role === "sponsor") && (
          <div style={box}>
            {label("FIND A TOURNAMENT")}
            {regions.length > 0 && (
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "10px 11px",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: "transparent",
                  color: C.white,
                  outline: "none",
                  marginBottom: 8,
                  fontSize: 11,
                }}
              >
                <option value="" style={{ color: "#000" }}>All regions</option>
                {regions.map((r) => (
                  <option key={r} value={r} style={{ color: "#000" }}>{r}</option>
                ))}
              </select>
            )}
            <input
              type="text"
              value={tournamentSearch}
              onChange={(e) => setTournamentSearch(e.target.value)}
              placeholder="Search by name or region..."
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 11px",
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: "transparent",
                color: C.white,
                outline: "none",
                marginBottom: 8,
                fontSize: 11,
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 240, overflowY: "auto" }}>
              {searchedTournaments.length === 0 ? (
                <div style={{ fontSize: 11, color: C.muted }}>
                  No active tournaments match your search.
                </div>
              ) : (
                searchedTournaments.map(pill)
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleShowTop = () => {
    const node = scrollContainerRef.current;
    if (!node) return;
    node.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLeaderboardBack = () => {
    if (selectedCatchId) {
      setSelectedCatchId("");
      return;
    }
    if (selectedTeamId) {
      setSelectedTeamId("");
      return;
    }
    handleShowTop();
  };

  if (!tournamentId) {
    return (
      <div ref={scrollContainerRef} className="hide-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "18px 14px 88px" }}>
        {isSelectableRole && accountHeader}
        <div style={{ fontSize: 11, color: C.teal, letterSpacing: 3, marginBottom: 3 }}>THE 99</div>
        <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700, marginBottom: 8 }}>
          Tournament Hub
        </div>
        {isSelectableRole ? (
          <>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
              {role === "director"
                ? "Select one of your tournaments to view its leaderboard."
                : "Search by name or browse by region to view a tournament's leaderboard."}
            </div>
            {renderTournamentPicker()}
          </>
        ) : (
          <Card>
            <div style={{ padding: 14, fontSize: 12, color: C.muted }}>
              No tournament is configured in your profile yet.
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className="hide-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "18px 14px 88px" }}>
      {isSelectableRole && accountHeader}
      <div style={{ fontSize: 11, color: C.teal, letterSpacing: 3, marginBottom: 3 }}>THE 99</div>
      <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700, marginBottom: 3 }}>
        Tournament Hub
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>{tournamentName || tournamentId}</div>

      {isSelectableRole && renderTournamentPicker()}

      {role === "director" && (
        <div style={{ marginBottom: 12 }}>
          <CollapsibleSection
            open={showExistingTeams}
            onToggle={() => setShowExistingTeams((prev) => !prev)}
            headerTitle={showExistingTeams ? "Hide existing teams" : "Show existing teams"}
            title={<span style={{ color: C.teal, letterSpacing: 2, fontWeight: 400 }}>ADD EXISTING TEAM</span>}
          >

                  <input
                    type="text"
                    value={teamSearch}
                    onChange={(e) => {
                      setTeamSearch(e.target.value);
                      setSelectedExistingTeamId("");
                      setAddTeamSuccess("");
                    }}
                    placeholder="Search team names..."
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "10px 11px",
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      background: C.cardBg,
                      color: C.white,
                      outline: "none",
                      marginTop: 8,
                      marginBottom: 8,
                      fontSize: 11,
                    }}
                  />

                  <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 170, overflowY: "auto", marginBottom: 8 }}>
                    {filteredExistingTeams.length === 0 ? (
                      <div style={{ fontSize: 11, color: C.muted }}>No teams match your search.</div>
                    ) : (
                      filteredExistingTeams.map((team) => (
                        <button
                          key={team.id}
                          type="button"
                          onClick={() => { setSelectedExistingTeamId(team.id); setAddTeamSuccess(""); }}
                          style={{
                            textAlign: "left",
                            borderRadius: 10,
                            border: `1px solid ${selectedExistingTeamId === team.id ? C.seafoam : "#1e3448"}`,
                            background: selectedExistingTeamId === team.id ? "rgba(46,191,165,0.12)" : "transparent",
                            color: C.white,
                            padding: "8px 10px",
                            cursor: "pointer",
                            fontSize: 11,
                          }}
                        >
                          {team.teamName}
                        </button>
                      ))
                    )}
                  </div>

                  {addTeamError && <div style={{ fontSize: 10, color: C.warning, marginBottom: 8 }}>{addTeamError}</div>}
                  {addTeamSuccess && <div style={{ fontSize: 10, color: C.seafoam, marginBottom: 8 }}>{addTeamSuccess}</div>}

                  <Button
                    onClick={handleAddExistingTeam}
                    disabled={!selectedExistingTeamId || addingTeam || !tournamentId}
                    variant="secondary"
                    size="sm"
                    fullWidth
                  >
                    {addingTeam ? "Adding..." : "Add Team to Selected Tournament"}
                  </Button>
          </CollapsibleSection>
        </div>
      )}

      {role === "director" && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.cardBg, marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: C.teal, letterSpacing: 2, marginBottom: 8 }}>TEAMS IN THIS TOURNAMENT</div>
          {removeTeamError && <div style={{ fontSize: 10, color: C.warning, marginBottom: 8 }}>{removeTeamError}</div>}
          {teams.length === 0 ? (
            <div style={{ fontSize: 11, color: C.muted }}>No teams associated with this tournament yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {teams.map((team) => (
                <div
                  key={team.id}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 10px" }}
                >
                  <span style={{ fontSize: 11, color: C.white, fontWeight: 700, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {team.teamName || team.id}
                  </span>
                  {confirmRemoveTeam?.id === team.id ? (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => setConfirmRemoveTeam(null)}
                        disabled={removingTeamId === team.id}
                        style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.white, fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveTeam(team)}
                        disabled={removingTeamId === team.id}
                        style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.warning}`, background: `${C.warning}22`, color: C.warning, fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                      >
                        {removingTeamId === team.id ? "Removing\u2026" : "Confirm remove"}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setConfirmRemoveTeam(team); setRemoveTeamError(""); }}
                      style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.warning}66`, background: "transparent", color: C.warning, fontSize: 10, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {role === "director" && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.cardBg, marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: C.teal, letterSpacing: 2, marginBottom: 8 }}>ADD SPONSOR</div>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 8, lineHeight: 1.5 }}>
            Link a sponsor to “{tournamentName || tournamentId}” by their account email. They'll
            see this tournament under the tournaments they sponsor.
          </div>
          <input
            type="email"
            value={sponsorEmailInput}
            onChange={(e) => {
              setSponsorEmailInput(e.target.value);
              setAddSponsorError("");
            }}
            placeholder="sponsor@example.com"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 11px",
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: "transparent",
              color: C.white,
              outline: "none",
              marginBottom: 8,
              fontSize: 11,
            }}
          />
          {addSponsorError && (
            <div style={{ fontSize: 10, color: C.warning, marginBottom: 8 }}>{addSponsorError}</div>
          )}
          <Button
            onClick={handleAddSponsor}
            disabled={addingSponsor || !sponsorEmailInput.trim() || !tournamentId}
            variant="secondary"
            size="sm"
            fullWidth
          >
            {addingSponsor ? "Adding..." : "Add Sponsor"}
          </Button>
          {Array.isArray(selectedTournamentDoc?.sponsorEmails) &&
            selectedTournamentDoc.sponsorEmails.length > 0 && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>CURRENT SPONSORS</div>
                {selectedTournamentDoc.sponsorEmails.map((em) => (
                  <div key={em} style={{ fontSize: 11, color: C.white }}>{em}</div>
                ))}
              </div>
            )}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <CollapsibleSection
          open={showTeams}
          onToggle={() => setShowTeams((prev) => !prev)}
          headerTitle={showTeams ? "Hide leaderboard" : "Show leaderboard"}
          subtitle={tournamentName || tournamentId}
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: C.teal, letterSpacing: 2, fontWeight: 400 }}>LEADERBOARD</span>
              <span
                style={{
                  fontSize: 8,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  color: C.seafoam,
                  border: `1px solid ${C.seafoam}66`,
                  background: `${C.seafoam}1f`,
                  borderRadius: 999,
                  padding: "2px 6px",
                  lineHeight: 1,
                }}
              >
                Beta
              </span>
            </span>
          }
        >
            {boardSponsors.length > 0 && (
              <SponsorBand sponsors={boardSponsors} />
            )}
            {!!leaderboardLoadError && (
              <div style={{ fontSize: 11, color: C.warning, marginBottom: 8 }}>{leaderboardLoadError}</div>
            )}
            {teams.length === 0 ? (
              <div style={{ fontSize: 11, color: C.muted }}>No teams registered yet.</div>
            ) : (
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", background: "linear-gradient(180deg, rgba(6,20,36,0.94), rgba(5,14,28,0.98))" }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "6px 10px",
                  background: "linear-gradient(90deg, rgba(6,31,50,0.98), rgba(13,54,90,0.95) 55%, rgba(6,31,50,0.98))",
                  borderBottom: "1px solid rgba(57,153,210,0.65)",
                }}>
                  <div style={{
                    fontSize: 8,
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    color: "#8dd4ff",
                    opacity: 0.9,
                  }}>
                    The 99 Online
                  </div>
                  <div style={{
                    padding: "4px 14px 3px",
                    borderRadius: 999,
                    border: "1px solid rgba(116,203,255,0.85)",
                    background: "linear-gradient(180deg, rgba(19,85,128,0.95), rgba(10,50,82,0.95))",
                    boxShadow: "0 0 0 1px rgba(8,28,46,0.9) inset, 0 0 14px rgba(57,153,210,0.25)",
                    color: "#d8f3ff",
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: 1.1,
                    textTransform: "uppercase",
                    lineHeight: 1,
                  }}>
                    Leaderboard
                  </div>
                  <div style={{
                    fontSize: 8,
                    color: "#75c0e8",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    opacity: 0.85,
                  }}>
                    Live
                  </div>
                </div>

                <div style={{
                  padding: "7px 10px",
                  borderBottom: `1px solid ${C.border}`,
                  background: "linear-gradient(90deg, rgba(16,53,84,0.95), rgba(7,25,45,0.95))",
                  color: "#d5ecff",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 1.1,
                  textTransform: "uppercase",
                }}>
                  Leaderboard
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "58px 1.7fr 1fr 1fr 1fr 1fr", padding: "6px 10px", color: "#82abc8", fontSize: 9, letterSpacing: 0.8, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>
                  <div>Position</div>
                  <div>Team</div>
                  <div>Top 1</div>
                  <div>Top 2</div>
                  <div>Top 3</div>
                  <div>{boardConfig.scoringMode === "aggregate" ? `Top ${boardConfig.aggregateTopN}` : "Total"}</div>
                </div>

                <div style={{ maxHeight: 320, overflowY: "auto" }}>
                  {visibleLeaderboardRows.map((row) => {
                    const team = row.team;
                    const isSelected = selectedTeamId === team.id;
                    const topThree = row.position != null && row.position <= 3;
                    const deductionLbs = boardConfig.showDeductions
                      ? (Number(row.deadCount) || 0) * boardConfig.deadFishDeductionLbs
                      : 0;

                    return (
                      <div
                        key={team.id}
                        style={{
                          borderBottom: "1px solid rgba(52,165,206,0.45)",
                          boxShadow: "inset 0 -1px 0 rgba(17,56,78,0.75), inset 0 1px 0 rgba(8,27,46,0.4)",
                          background: row.hasNew
                            ? "rgba(224,48,48,0.1)"
                            : topThree
                              ? "rgba(46,191,165,0.08)"
                              : isSelected
                                ? "rgba(65,158,219,0.1)"
                                : "transparent",
                          padding: "7px 10px",
                        }}
                      >
                        <div style={{ display: "grid", gridTemplateColumns: "58px 1.7fr 1fr 1fr 1fr 1fr", alignItems: "center", columnGap: 4 }}>
                          <div style={{ fontSize: 10, color: "#9cd1f5", fontWeight: 700 }}>{row.position != null ? `#${row.position}` : "\u2014"}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: C.white, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {team.teamName || team.id}
                              {row.disqualified && (
                                <span style={{ marginLeft: 6, fontSize: 8, fontWeight: 800, color: C.red, border: `1px solid ${C.red}`, borderRadius: 4, padding: "1px 4px", letterSpacing: 0.5 }}>DQ</span>
                              )}
                            </div>
                            <div style={{ fontSize: 9, color: "#7f9cb4", marginTop: 2 }}>
                              {row.catchCount} catches
                              {boardConfig.bigFish && row.bigFishOz > 0 && (
                                <span style={{ marginLeft: 6, color: "#9dd8ff" }}>
                                  · Big {formatBoardWeight(row.bigFishOz, boardConfig.weightUnit)}
                                </span>
                              )}
                              {deductionLbs > 0 && (
                                <span style={{ marginLeft: 6, color: C.warning }}>
                                  · −{deductionLbs.toFixed(2)} lb dead
                                </span>
                              )}
                              {boatAssignmentsMap[team.id]?.boatNumber != null && boatAssignmentsMap[team.id]?.boatNumber !== "" && (
                                <span style={{ marginLeft: 6, color: "#9dd8ff" }}>
                                  · Boat #{boatAssignmentsMap[team.id].boatNumber}
                                  {boatAssignmentsMap[team.id].confirmed ? " \u2713" : ""}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ fontSize: 10, color: C.white }}>{formatBoardWeight(row.rounds[0], boardConfig.weightUnit)}</div>
                          <div style={{ fontSize: 10, color: C.white }}>{formatBoardWeight(row.rounds[1], boardConfig.weightUnit)}</div>
                          <div style={{ fontSize: 10, color: C.white }}>{formatBoardWeight(row.rounds[2], boardConfig.weightUnit)}</div>
                          <div style={{ fontSize: 10, color: row.hasNew ? C.red : C.seafoam, fontWeight: 800 }}>
                            {formatBoardWeight(row.totalOz, boardConfig.weightUnit)}
                          </div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                          {role === "participant" && (
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, color: C.white, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={followingTeamIds.includes(team.id)}
                                onChange={() => toggleFollowTeam(team.id)}
                                disabled={saving}
                                style={{ width: 14, height: 14 }}
                              />
                              Follow
                            </label>
                          )}
                          {role === "director" && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedTeamId(team.id);
                                setSeenCatchIdsByTeam((prev) => ({
                                  ...prev,
                                  [team.id]: (catchesByTeam[team.id] || []).map((item) => item.id),
                                }));
                              }}
                              style={{
                                padding: "5px 9px",
                                borderRadius: 8,
                                border: `1px solid ${row.hasNew ? C.red : isSelected ? C.seafoam : "#1e3448"}`,
                                background: row.hasNew
                                  ? "rgba(224,48,48,0.18)"
                                  : isSelected
                                    ? "rgba(46,191,165,0.12)"
                                    : "transparent",
                                color: row.hasNew ? C.red : C.white,
                                cursor: "pointer",
                                fontSize: 10,
                                fontWeight: row.hasNew ? 800 : 600,
                              }}
                            >
                              View Catches
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{
                  borderTop: "1px solid rgba(44,145,191,0.55)",
                  background: "linear-gradient(180deg, rgba(8,26,45,0.96), rgba(5,17,31,0.98))",
                  padding: "8px 10px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                  <button
                    type="button"
                    onClick={handleLeaderboardBack}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#9dd8ff",
                      fontSize: 10,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      fontWeight: 700,
                      padding: 0,
                    }}
                  >
                    <span style={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      border: "1px solid #1f85bd",
                      background: "rgba(9,51,84,0.9)",
                      boxShadow: "0 0 6px rgba(42,154,209,0.55)",
                    }} />
                    Back
                  </button>

                  <button
                    type="button"
                    onClick={handleShowTop}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#9dd8ff",
                      fontSize: 10,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      fontWeight: 700,
                      padding: 0,
                    }}
                  >
                    <span style={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      border: "1px solid #1f85bd",
                      background: "rgba(9,51,84,0.9)",
                      boxShadow: "0 0 6px rgba(42,154,209,0.55)",
                    }} />
                    Show Top
                  </button>
                </div>
              </div>
            )}
        </CollapsibleSection>
      </div>

      {role === "director" && selectedTeam && (
        <div style={{ marginBottom: 12 }}>
          <CollapsibleSection
            open={showCatches}
            onToggle={() => setShowCatches((prev) => !prev)}
            headerTitle={showCatches ? "Hide catches" : "Show catches"}
            title={<span style={{ color: C.teal, letterSpacing: 2, fontWeight: 400 }}>CATCHES · {selectedTeam.teamName || selectedTeam.id}</span>}
          >
              {loadingCatches ? (
                <div style={{ fontSize: 11, color: C.muted }}>Loading catches...</div>
              ) : selectedTeamCatches.length === 0 ? (
                <div style={{ fontSize: 11, color: C.muted }}>No catches found for this team.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {selectedTeamCatches.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedCatchId(item.id)}
                      style={{
                        textAlign: "left",
                        borderRadius: 10,
                        border: `1px solid ${selectedCatchId === item.id ? C.seafoam : "#1e3448"}`,
                        background: selectedCatchId === item.id ? "rgba(46,191,165,0.12)" : "transparent",
                        color: C.white,
                        padding: "9px 10px",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 700 }}>{item.species}</span>
                        <span style={{ fontSize: 10, color: C.muted }}>{item.weight} · {item.length}</span>
                      </div>
                      {formatCaughtAt(item.caughtAt) && (
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
                          {formatCaughtAt(item.caughtAt)}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
        </CollapsibleSection>
        </div>
      )}

      {role === "director" && selectedCatch && (
        <div style={{ marginBottom: 12 }}>
          <Card>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 10, color: C.teal, letterSpacing: 2, marginBottom: 8 }}>CATCH SUMMARY</div>
              <div style={{ fontSize: 11, color: C.white, marginBottom: 5 }}>Species: {selectedCatch.species}</div>
              <div style={{ fontSize: 11, color: C.white, marginBottom: 5 }}>Weight: {selectedCatch.weight}</div>
              <div style={{ fontSize: 11, color: C.white, marginBottom: 5 }}>Length: {selectedCatch.length}</div>
              <div style={{ fontSize: 11, color: C.white, marginBottom: 10 }}>
                Boundary Check: <span style={{ color: C.seafoam }}>{selectedCatch.boundaryStatus}</span>
                <span style={{ color: C.muted }}> : FALSE (placeholder)</span>
              </div>

              {(selectedVideoUrl || selectedCatch.videoStoragePath || selectedCatch.videoUrl) ? (
                selectedVideoUrl ? (
                  <>
                    <a
                      href={selectedVideoUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: C.seafoam, fontSize: 11, textDecoration: "none", display: "inline-block", marginBottom: 8 }}
                    >
                      Open Video Link
                    </a>
                    <video
                      controls
                      playsInline
                      src={selectedVideoUrl}
                      style={{ width: "100%", borderRadius: 10, border: `1px solid ${C.border}`, background: "#000" }}
                    />
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: C.muted }}>Loading video…</div>
                )
              ) : (
                <div style={{ fontSize: 11, color: C.muted }}>No video available for this catch.</div>
              )}
            </div>
          </Card>
        </div>
      )}

      <div>
        <Card>
          <div style={{ padding: 14 }}>
            <div style={{ fontSize: 10, color: C.teal, letterSpacing: 2, marginBottom: 8 }}>
              {role === "participant" ? "TEAM VIDEOS" : "ALL TOURNAMENT VIDEOS"}
            </div>
            {visibleVideos.length === 0 ? (
              <div style={{ fontSize: 11, color: C.muted }}>No videos available yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {visibleVideos.map((video) => {
                  const isExpanded = expandedVideoIds.has(video.id);
                  const c = video.catch;
                  const species = c?.species && c.species !== "—" ? c.species : null;
                  const weight = c?.weight && c.weight !== "—" ? c.weight
                    : c?.weightLbs != null ? `${c.weightLbs} lbs` : null;
                  const length = c?.size && c.size !== "—" ? c.size
                    : c?.length && c.length !== "—" ? c.length
                    : c?.lengthIn != null ? `${c.lengthIn}"` : null;
                  const parts = [
                    species ? `Species: ${species}` : null,
                    weight ? `Weight: ${weight}` : null,
                    length ? `Length: ${length}` : null,
                  ].filter(Boolean);
                  const toggle = () => {
                    setExpandedVideoIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(video.id)) next.delete(video.id);
                      else next.add(video.id);
                      return next;
                    });
                  };
                  return (
                    <div
                      key={video.id}
                      style={{
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        background: C.midnight,
                        overflow: "hidden",
                      }}
                    >
                      <button
                        type="button"
                        onClick={toggle}
                        style={{
                          width: "100%",
                          background: "transparent",
                          border: "none",
                          padding: "10px 12px",
                          cursor: "pointer",
                          textAlign: "left",
                          color: C.white,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                          <ProfileAvatar
                            url={anglerPhotos[video.userId]?.photoURL || ""}
                            name={anglerPhotos[video.userId]?.displayName || video.teamName || ""}
                            size={26}
                          />
                          <div style={{ fontSize: 12, color: C.white, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {video.catch?.species || "Catch Video"}
                          </div>
                        </div>
                        <div style={{ fontSize: 10, color: C.white, opacity: 0.92, flexShrink: 0 }}>{video.teamName || "No Team"}</div>
                        <div style={{ fontSize: 10, color: C.muted, flexShrink: 0, whiteSpace: "nowrap" }} title="Views">
                          👁 {video.viewCount || 0}
                        </div>
                        <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{isExpanded ? "↑" : "↓"}</span>
                      </button>
                      {isExpanded && (
                        <ExpandedVideoBody video={video} parts={parts} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
